// frontend/src/components/Analysis/AnalysisResults.tsx
import React, { useState } from 'react';
import { Activity, ChevronDown, ChevronUp, FileText, Info, Building, Sparkles, BookOpen } from 'lucide-react';

interface DocumentAnalysis {
  id: number;
  document_name: string;
  category: string;
  summary: string;
  executive_summary?: string;
  confidence_score: number;
  template_compliance?: {
    compliance_score: number;
    compliant_areas?: string[];
    gaps?: string[];
    recommendations?: string[];
  };
  key_points?: {
    key_processes: string[];
    controls_identified: string[];
    roles_responsibilities: { [key: string]: string };
  };
  business_context?: {
    primary_purpose: string;
    business_areas: string[];
    activities_covered: string[];
    scope_limitations: string[];
    stakeholders: string[];
  };
  document_metadata?: {
    document_type: string;
    complexity_level: string;
    completeness: string;
  };
  executive_summary_data?: any;
  Document?: {
    original_name: string;
    category: string;
  };
}

interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  analyzing: number;
  total: number;
 
}

interface AnalysisResultsProps {
  analysisResults: {
    document_analyses: DocumentAnalysis[];
    analysis_summary?: {
      executive_summary?: string;
      total_documents?: number;
    };
  };
  isAnalyzing?: boolean;
  analysisProgress?: AnalysisProgress | null;
  setActiveTab: (tab: string) => void;
   onStartAnalysis?: (fullAnalysis?: boolean) => void;
}

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  placement?: 'top' | 'bottom';
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, placement = 'bottom' }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState('left-1/2 -translate-x-1/2');
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (visible && tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current;
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      // Calculate if tooltip would be cut off on the right
      const tooltipRightEdge = containerRect.left + tooltipRect.width / 2;
      const tooltipLeftEdge = containerRect.left - tooltipRect.width / 2;
      
      if (tooltipRightEdge > viewportWidth - 20) {
        // Tooltip would be cut off on the right, align it to the right edge
        setPosition('right-0');
      } else if (tooltipLeftEdge < 20) {
        // Tooltip would be cut off on the left, align it to the left edge
        setPosition('left-0');
      } else {
        // Tooltip fits, center it
        setPosition('left-1/2 -translate-x-1/2');
      }
    }
  }, [visible]);
  
  const tooltipStyle = {
    whiteSpace: 'pre-line' as const,
    maxWidth: '24rem',
    minWidth: '16rem',
    padding: '1rem',
    background: '#111827',
    color: '#fff',
    borderRadius: '0.75rem',
    fontSize: '0.75rem',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    wordBreak: 'normal' as const,
    overflowX: 'auto' as const,
    border: '1px solid #22223b',
  };

  return (
    <span 
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      tabIndex={0}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 ${position} ${
            placement === 'top' ? 'mb-2 bottom-full' : 'mt-2 top-full'
          }`}
          style={tooltipStyle}
        >
          {text}
        </div>
      )}
    </span>
  );
};

const DocumentAnalysisCard: React.FC<{ analysis: DocumentAnalysis, setActiveTab : (tab: string) => void; }> = ({ analysis, setActiveTab }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullSummary, setShowFullSummary] = useState(false);
  const summaryLines = (analysis.executive_summary || 'Executive summary generation in progress...').split('\n');
  const displayedLines = showFullSummary ? summaryLines : summaryLines.slice(0, 3);
  const [showFullDetailedSummary, setShowFullDetailedSummary] = useState(false);

  const getConfidenceColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getConfidenceLabel = (score: number) => {
    if (score >= 80) return 'High Confidence';
    if (score >= 60) return 'Moderate Confidence';
    return 'Low Confidence';
  };

  const confidenceTooltip = `This score (0–100) reflects the AI's confidence in its analysis of this document.\nIt is based on the clarity, completeness, and structure of the document, and how well it matches expected process and control requirements. Higher scores mean the AI found clear, well-structured information; lower scores indicate uncertainty or missing details.`;
  
  const complianceTooltip = `This score (0–100) measures how closely the document matches the required sections and standards of the selected template.\nIt considers which areas are compliant, which have gaps, and provides recommendations for improvement. Higher scores mean better alignment with the template.`;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Document Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {analysis.Document?.original_name || analysis.document_name}
          </h3>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {analysis.Document?.category || analysis.category}
            </span>
            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceColor(analysis.confidence_score)}`}>
              <Activity className="w-3 h-3 mr-1" />
              {getConfidenceLabel(analysis.confidence_score)} ({analysis.confidence_score}%)
            </div>
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Executive Summary - Always Visible */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-gray-900">Executive Summary</h4>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <p className="text-gray-700 leading-relaxed">
            {displayedLines.map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </p>
          {summaryLines.length > 3 && (
            <button
              className="mt-2 text-blue-600 hover:underline text-sm"
              onClick={() => setShowFullSummary((prev) => !prev)}
            >
              {showFullSummary ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>

      {/* Business Context Overview - Hidden: Purpose removed as duplicate of Executive Summary */}
      {/* Business Areas moved to expanded section below Controls Identified */}

      {/* Confidence and Compliance Scores */}
      {/* Hidden: Confidence Score and Template Compliance badges - as per client request */}
      {false && <div className="flex items-center space-x-4 mb-4">

        <span onClick={() => setActiveTab('Confidence Scoring') } className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm flex items-center space-x-1 cursor-pointer">
          Confidence Score: {analysis.confidence_score ?? 'N/A'}%
          <Tooltip text={confidenceTooltip}>
            <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </span>
        <span onClick={() => setActiveTab('Confidence Scoring') } className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm flex items-center space-x-1 cursor-pointer">
          Template Compliance: {analysis.template_compliance?.compliance_score ?? 'N/A'}%
          <Tooltip text={complianceTooltip}>
            <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </span>
      </div>}

      {/* Expandable Details */}
      {isExpanded && (
        <div className="space-y-6 pt-4 border-t border-gray-200">
          {/* Full Summary */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-gray-900">Detailed Summary</h4>
            </div>
            <div className="bg-purple-100 border-l-4 border-purple-400 p-4 rounded-r-lg">
              {(() => {
                // Split summary into sentences for better truncation
                const detailedSummaryLines = (analysis.summary || 'No detailed summary available.').match(/[^.!?]+[.!?]+/g) || [];
                const displayedDetailedSummary = showFullDetailedSummary ? detailedSummaryLines : detailedSummaryLines.slice(0, 3);

                return (
                  <>
                    <p className="text-gray-800 leading-relaxed" style={{ lineHeight: '1.8' }}>
                      {displayedDetailedSummary}
                    </p>
                    {detailedSummaryLines.length > 3 && (
                      <button
                        className="mt-3 text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center gap-1"
                        onClick={() => setShowFullDetailedSummary((prev) => !prev)}
                      >
                        {showFullDetailedSummary ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          {/* Key Processes, Controls Identified, and Business Areas - always show when expanded */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-medium flex items-center">
                  Key Processes
                  <Tooltip text="AI extracts distinct process steps from document content. Count varies by complexity: Simple docs (2-4 processes), Complex docs (5-10 processes), Comprehensive SOPs (8-15 processes). No fixed limits - results are document-specific." placement="top">
                    <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
                  </Tooltip>
                </h4>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {((analysis.key_points?.key_processes?.length ?? 0)
                  || (Array.isArray((analysis as any).key_processes) && (analysis as any).key_processes.length)
                  || 0)} processes extracted from document content
              </p>
              <ul className="list-disc ml-5 space-y-1">
                {(analysis.key_points?.key_processes?.length ?? 0) > 0
                  ? analysis.key_points?.key_processes?.map((process: any, idx: number) => (
                      <li key={idx} className="text-sm text-gray-700">
                        {typeof process === 'string'
                          ? process
                          : process && typeof process === 'object' && 'text' in process
                            ? process.text + (process.page ? ` (p.${process.page})` : '')
                            : ''}
                      </li>
                    ))
                  : (Array.isArray((analysis as any).key_processes) && (analysis as any).key_processes.length > 0)
                    ? (analysis as any).key_processes.map((process: any, idx: number) => (
                        <li key={idx} className="text-sm text-gray-700">
                          {typeof process === 'string'
                            ? process
                            : process && typeof process === 'object' && 'text' in process
                              ? process.text + (process.page ? ` (p.${process.page})` : '')
                              : ''}
                        </li>
                      ))
                    : <li className="text-sm text-gray-500 italic">No key processes found.</li>}
              </ul>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-medium flex items-center">
                  Controls Identified
                  <Tooltip text="AI identifies control points and approval requirements from this document. Typical ranges: Simple docs (1-3 controls), Complex docs (3-8 controls), Comprehensive SOPs (5-12 controls). Includes preventive, detective, and corrective controls." placement="top">
                    <Info className="inline-block align-middle w-4 h-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
                  </Tooltip>
                </h4>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {((analysis.key_points?.controls_identified?.length ?? 0)
                  || (Array.isArray((analysis as any).controls_identified) && (analysis as any).controls_identified.length)
                  || 0)} controls extracted from document content
              </p>
              <ul className="list-disc ml-5 space-y-1">
                {(analysis.key_points?.controls_identified?.length ?? 0) > 0
                  ? analysis.key_points?.controls_identified?.map((control: any, idx: number) => (
                      <li key={idx} className="text-sm text-gray-700">
                        {typeof control === 'string'
                          ? control
                          : control && typeof control === 'object' && 'text' in control
                            ? control.text + (control.page ? ` (p.${control.page})` : '')
                            : ''}
                      </li>
                    ))
                  : (Array.isArray((analysis as any).controls_identified) && (analysis as any).controls_identified.length > 0)
                    ? (analysis as any).controls_identified.map((control: any, idx: number) => (
                        <li key={idx} className="text-sm text-gray-700">
                          {typeof control === 'string'
                            ? control
                            : control && typeof control === 'object' && 'text' in control
                              ? control.text + (control.page ? ` (p.${control.page})` : '')
                              : ''}
                        </li>
                      ))
                    : <li className="text-sm text-gray-500 italic">No controls identified.</li>}
              </ul>
            </div>

            {/* Business Areas - third column */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-medium">Business Areas</h4>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {analysis.business_context?.business_areas?.length ?? 0} areas identified
              </p>
              <ul className="list-disc ml-5 space-y-1">
                {analysis.business_context?.business_areas && analysis.business_context.business_areas.length > 0
                  ? analysis.business_context.business_areas.map((area, idx) => (
                      <li key={idx} className="text-sm text-gray-700">{area}</li>
                    ))
                  : <li className="text-sm text-gray-500 italic">No business areas found.</li>}
              </ul>
            </div>
          </div>

          {/* Roles and Responsibilities */}
          {analysis.key_points?.roles_responsibilities && Object.keys(analysis.key_points.roles_responsibilities).length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Roles & Responsibilities</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(analysis.key_points.roles_responsibilities).slice(0, 4).map(([role, responsibility], idx) => (
                  <div key={idx} className="bg-gray-50 p-2 rounded">
                    <span className="font-medium text-sm text-gray-900">{role}:</span>
                    <span className="text-sm text-gray-700 ml-1">{responsibility}</span>
                  </div>
                ))}
                {Object.keys(analysis.key_points.roles_responsibilities).length > 4 && (
                  <div className="text-sm text-gray-500 italic">
                    +{Object.keys(analysis.key_points.roles_responsibilities).length - 4} more roles...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Document Metadata */}
          {analysis.document_metadata && (
            <div>
              <h4 className="font-medium mb-2">Document Metadata</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <div className="text-gray-600">{analysis.document_metadata.document_type || 'Unknown'}</div>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Complexity:</span>
                  <div className="text-gray-600">{analysis.document_metadata.complexity_level || 'Unknown'}</div>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Completeness:</span>
                  <div className="text-gray-600">{analysis.document_metadata.completeness || 'Unknown'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Document Strengths and Weaknesses - HIDDEN as per client request */}
          {/* This section has been disabled and hidden from the UI as per client request */}

          
          {/* {analysis.business_context && (
            <div>
              <h4 className="font-medium mb-2">Complete Business Context</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.business_context.activities_covered && analysis.business_context.activities_covered.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-1">Activities Covered</h5>
                    <ul className="list-disc ml-5 text-sm text-gray-700">
                      {analysis.business_context.activities_covered.map((activity, idx) => (
                        <li key={idx}>{activity}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.business_context.stakeholders && analysis.business_context.stakeholders.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h5 className="font-medium text-gray-900 mb-1">Stakeholders</h5>
                    <ul className="list-disc ml-5 text-sm text-gray-700">
                      {analysis.business_context.stakeholders.map((stakeholder, idx) => (
                        <li key={idx}>{stakeholder}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ 
  analysisResults, 
  isAnalyzing = false, 
  analysisProgress = null,
  setActiveTab, 
  onStartAnalysis 
}) => {
  // Show loading state while analysis is in progress
  if (isAnalyzing && analysisProgress) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Document Analysis Results</h2>
        
        <div className="flex flex-col items-center justify-center py-16 max-w-lg mx-auto">
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-6 shadow-inner">
            <div 
              className="bg-gradient-to-r from-electric-blue to-purple-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ width: `${analysisProgress.progress}%` }}
            ></div>
          </div>
          
          {/* Loading Animation */}
          <div className="relative mb-6">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-electric-blue border-t-transparent absolute top-0 left-0"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FileText className="w-8 h-8 text-electric-blue animate-pulse" />
            </div>
          </div>
          
          {/* Status Message */}
          <div className="text-center mb-4">
            <p className="text-gray-700 text-lg font-medium mb-2">
              🔍 Analyzing documents to generate comprehensive summaries...
            </p>
            <div className="flex items-center justify-center space-x-2 text-electric-blue">
              <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-electric-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
          
          {/* Progress Details */}
          <div className="text-sm text-gray-600 text-center bg-gray-50 rounded-lg p-4 w-full">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
              <div>
                <span className="font-semibold text-green-600">{analysisProgress.completed}</span>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
              <div>
                <span className="font-semibold text-blue-600">{analysisProgress.analyzing}</span>
                <p className="text-xs text-gray-500">Analyzing</p>
              </div>
              <div>
                <span className="font-semibold text-gray-600">{analysisProgress.pending}</span>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
              <div>
                <span className="font-semibold text-gray-800">{analysisProgress.total}</span>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 bg-blue-50 rounded p-3 border-l-4 border-blue-300">
              <p className="mb-1">📄 <strong>Processing:</strong> AI is extracting content, identifying processes, and analyzing compliance for each document</p>
              <p className="mb-1">🎯 <strong>Output:</strong> Executive summaries, business context, key processes, and control points</p>
              <p>⏱️ <strong>Duration:</strong> 1-3 minutes per document depending on complexity</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show "Start Analysis" when analysis is complete but no results yet
if (!isAnalyzing && analysisProgress && analysisProgress.total > 0 && !analysisResults?.document_analyses?.length) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Document Analysis Results</h2>
      
      <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300">
        <div className="max-w-md mx-auto">
          <FileText className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Ready to Start Analysis</h3>
          <p className="text-gray-600 mb-4">
            You have {analysisProgress.total} document{analysisProgress.total !== 1 ? 's' : ''} uploaded and ready for analysis.
          </p>
          <button
            onClick={() => onStartAnalysis && onStartAnalysis(false)}
            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 mx-auto"
          >
            <FileText className="w-4 h-4" />
            <span>Start Document Analysis</span>
          </button>
        </div>
      </div>
    </div>
  );
}

  // Show empty state when no analysis has been run
  if (!analysisResults?.document_analyses?.length) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Document Analysis Results</h2>
        
        <div className="text-center py-16">
          <div className="bg-gray-100 rounded-full h-16 w-16 mx-auto mb-6 flex items-center justify-center">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Document Analyses Yet</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Run the analysis process to generate comprehensive document summaries, extract key processes, 
            and identify control points across your uploaded documents.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-blue-800 text-sm">
              💡 <strong>Tip:</strong> Document summaries will show executive summaries, business context, 
              compliance scores, and detailed breakdowns for each analyzed document.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if we should show combined summary (more than 1 document)
  const showCombinedSummary =
    analysisResults?.analysis_summary?.executive_summary &&
    analysisResults.analysis_summary.executive_summary.trim().length > 0 &&
    (analysisResults?.analysis_summary?.total_documents || 0) > 1;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Document Analysis Results</h2>

      {/* Combined Summary - Only show when more than 1 document */}
      {showCombinedSummary && (
        <div className="bg-purple-100 border-2 border-purple-600 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-6 h-6 text-purple-700" />
            <h3 className="text-lg font-semibold text-purple-900">Combined Summary</h3>
          </div>
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">
            {analysisResults.analysis_summary?.executive_summary}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {analysisResults.document_analyses.map((analysis: DocumentAnalysis, index: number) => (
          <DocumentAnalysisCard key={analysis.id || index} analysis={analysis} setActiveTab={setActiveTab} />
        ))}
      </div>
    </div>
  );
};

export default AnalysisResults;